// A table answer in Ask Ogma's side window (Craig, 31 Oct 2026: one Ask page, any table opens in the pane; it replaced
// Ask the CRM at /ask/table). The supervisor's table carries its query spec; everything after that (removing or changing
// a filter, sorting, choosing columns, exporting, saving as a view, acting on the list) re-runs the spec directly with
// crm_ask_query, without the model. Nothing happens to a list until the person confirms, and emails still wait in
// Approvals (gate 4.10).
import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useCrm } from "../../lib/session";
import {
  columnLabel,
  exportName,
  isViewSource,
  filterLabel,
  NO_VALUE_OPS,
  OP_LABELS,
  opsForType,
  parseValue,
  toggleSort,
  toTsv,
  visibleColumns,
  withColumns,
  withFilter,
  withFilterValue,
  withoutFilter,
  type AskColumn,
  type AskResult,
  type AskSpec,
} from "../../lib/ask";
import { saveView, type RecordSource, type RecordSpec, type SavedView } from "../../lib/records";
import { exportView } from "../../lib/exportView";
import { ACTION_KINDS, ACTION_LABELS, blankAction, type ActionKind, type ActionReply } from "../../lib/askActions";
import { ActionCard } from "../ActionCard";
import { fmtDate, fmtNumber, humanise } from "../../lib/format";
import { ErrorState, Notice } from "../ui";

const NOUN: Record<string, string> = { firms: "firms", people: "people", activity: "activity", deals: "deals", meetings: "meetings", events: "events", industry_events: "industry events", networks: "networks", partners: "partners" };

type Busy = "running" | "exporting" | null;

/** An Ask spec as a list's spec, for a saved view (the limit is Ask's own). */
export function viewSpecFromAsk(spec: AskSpec): RecordSpec {
  return {
    source: spec.source as RecordSource,
    filters: spec.filters ?? [],
    ...(spec.sort?.length ? { sort: spec.sort } : {}),
    ...(spec.columns?.length ? { columns: spec.columns } : {}),
  };
}

/** Where a saved view lives: its list page, opened on that view. */
export const viewHref = (v: { id: string; source: string }) => `${v.source === "people" ? "/contacts/people" : "/contacts/companies"}?view=${v.id}`;

/** The table behind one of Ask Ogma's answers, live: it runs the answer's spec and re-runs it as the person changes it. */
export function AskTable({ spec, title }: { spec: AskSpec; title: string }) {
  const crm = useCrm();
  const [result, setResult] = useState<AskResult | null>(null);
  const [action, setAction] = useState<ActionReply | null>(null);
  const [actionNonce, setActionNonce] = useState(0);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<{ text: string; retry: () => void } | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const [savedView, setSavedView] = useState<SavedView | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [schema, setSchema] = useState<Record<string, AskColumn[]>>({});

  const run = useCallback(
    async (next: AskSpec) => {
      setBusy("running");
      setError(null);
      setNotice(null);
      try {
        setResult(await crm.askQuery(next));
        setAction((a) => (a ? { ...a, preview: null, error: null } : a));
      } catch (e) {
        setError({ text: (e as Error).message, retry: () => void run(next) });
      } finally {
        setBusy(null);
      }
    },
    [crm],
  );
  useEffect(() => {
    void run(spec);
  }, [run, spec]);

  // The source's columns, for the add-filter and column pickers. Loaded once per source.
  const source = result?.spec.source;
  useEffect(() => {
    if (!source || schema[source]) return;
    let alive = true;
    crm
      .askColumns(source)
      .then((cols) => alive && setSchema((s) => ({ ...s, [source]: cols ?? [] })))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [crm, source, schema]);

  // A list of firms or people is saved as a view on Companies or People (3.12), on any device.
  const save = async (name: string, shared: boolean) => {
    if (!result || !isViewSource(result.spec.source)) return;
    setNotice(null);
    setSavedView(null);
    const src = result.spec.source;
    const columns = result.spec.columns?.length ? result.spec.columns : visibleColumns(result.columns).map((c) => c.name);
    try {
      const v = await saveView(crm, { source: src, name, spec: viewSpecFromAsk({ ...result.spec, columns }), shared });
      setSavedView(v);
      setNotice({ text: `Saved "${v.name}" as a view on ${src === "people" ? "People" : "Companies"}${v.shared ? " for everyone in the business" : ", just for you"}.`, tone: "ok" });
    } catch (e) {
      setNotice({ text: (e as Error).message, tone: "bad" });
    }
  };

  // The whole answer, no row cap (E72)
  const exportCsv = async () => {
    if (!result) return;
    setBusy("exporting");
    setNotice(null);
    setProgress({ done: 0, total: result.total });
    try {
      const shown = visibleColumns(result.columns);
      const { limit: _limit, ...rest } = result.spec;
      const r = await exportView(crm, {
        spec: { ...rest, columns: shown.map((c) => c.name) },
        filename: exportName(result.spec),
        columns: () => shown.map((c) => ({ header: columnLabel(c.name), value: (row: Record<string, unknown>) => row[c.name] })),
        onProgress: setProgress,
      });
      setNotice({ text: `Downloaded ${fmtNumber(r.rows)} ${r.rows === 1 ? "row" : "rows"}, the whole answer.`, tone: "ok" });
    } catch (e) {
      setNotice({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const copyTable = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(toTsv(visibleColumns(result.columns), result.rows));
      setNotice({ text: `Copied ${fmtNumber(result.rows.length)} rows. Paste them into a spreadsheet or a document.`, tone: "ok" });
    } catch {
      setNotice({ text: "This browser didn't allow copying. Use Download CSV instead.", tone: "bad" });
    }
  };

  if (error && !result) return <ErrorState error={error.text} onRetry={error.retry} />;
  if (!result) {
    return (
      <div className="state" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>Reading your CRM…</span>
      </div>
    );
  }
  return (
    <section className="ask-result" aria-live="polite" aria-busy={busy !== null}>
      {error && <ErrorState error={error.text} onRetry={error.retry} />}
      <ResultView
        key={String(result.spec.source)}
        result={result}
        asked={title}
        busy={busy}
        schema={schema[String(result.spec.source)] ?? []}
        notice={notice}
        savedView={savedView}
        progress={progress}
        onRun={(s) => void run(s)}
        onSave={(name, shared) => void save(name, shared)}
        onExport={() => void exportCsv()}
        onCopy={() => void copyTable()}
        onAct={(kind) => {
          setAction(blankAction(kind));
          setActionNonce((n) => n + 1);
        }}
        card={
          action && (result.spec.source === "people" || result.spec.source === "firms") ? (
            <ActionCard key={`${JSON.stringify([result.spec.filters, result.spec.sort, result.spec.limit])}|${action.action}|${actionNonce}`} crm={crm} spec={result.spec} initial={action} onClose={() => setAction(null)} />
          ) : action?.error ? (
            <Notice text={action.error} tone="bad" />
          ) : null
        }
      />
    </section>
  );
}

// ------------------------------------------------------------------ the result
function ResultView({
  result,
  asked,
  busy,
  schema,
  notice,
  savedView,
  progress,
  onRun,
  onSave,
  onExport,
  onCopy,
  onAct,
  card,
}: {
  result: AskResult;
  asked: string | null;
  busy: Busy;
  schema: AskColumn[];
  notice: { text: string; tone: "ok" | "bad" } | null;
  savedView: SavedView | null;
  progress: { done: number; total: number } | null;
  onRun: (spec: AskSpec) => void;
  onSave: (name: string, shared: boolean) => void;
  onExport: () => void;
  onCopy: () => void;
  onAct: (kind: ActionKind) => void;
  card: ReactNode;
}) {
  const { spec } = result;
  const cols = visibleColumns(result.columns);
  const [panel, setPanel] = useState<"filter" | "columns" | "save" | null>(null);
  const noun = NOUN[String(spec.source)] ?? "rows";
  const shown = result.rows.length;

  return (
    <div className={`ask-answer${busy === "running" ? " is-stale" : ""}`}>
      <header className="ask-answer-head">
        <h2 className="ask-summary">{result.summary}</h2>
      </header>

      <div className="ask-filters" aria-label="Filters">
        <span className="ask-filters-lead">
          {humanSource(String(spec.source))}
          {spec.filters.length ? " where" : ", no filters"}
        </span>
        <ul>
          {spec.filters.map((f, i) => (
            <li key={`${i}-${f.field}-${f.op}`}>
              <FilterChip
                filter={f}
                disabled={busy !== null}
                onChange={(value) => onRun(withFilterValue(spec, i, value))}
                onRemove={() => onRun(withoutFilter(spec, i))}
              />
            </li>
          ))}
          <li>
            <button type="button" className="ask-add" aria-expanded={panel === "filter"} onClick={() => setPanel(panel === "filter" ? null : "filter")}>
              + Add a filter
            </button>
          </li>
        </ul>
      </div>
      {panel === "filter" && (
        <AddFilter
          schema={schema}
          onAdd={(f) => {
            setPanel(null);
            onRun(withFilter(spec, f));
          }}
          onCancel={() => setPanel(null)}
        />
      )}

      <div className="ask-bar">
        <p className="ask-count">
          {result.total === 0
            ? "No rows"
            : result.truncated
              ? `Showing the first ${fmtNumber(shown)} of ${fmtNumber(result.total)}`
              : `${fmtNumber(result.total)} ${result.total === 1 ? "row" : "rows"}`}
        </p>
        <div className="ask-actions">
          <button type="button" className="btn btn-quiet" aria-expanded={panel === "columns"} onClick={() => setPanel(panel === "columns" ? null : "columns")}>
            Columns
          </button>
          {isViewSource(spec.source) && (
            <button type="button" className="btn btn-quiet" aria-expanded={panel === "save"} onClick={() => setPanel(panel === "save" ? null : "save")}>
              Save as a view
            </button>
          )}
          <button type="button" className="btn btn-quiet" onClick={onCopy} disabled={!shown}>
            Copy as table
          </button>
          <button type="button" className="btn" onClick={onExport} disabled={!result.total || busy !== null}>
            {busy === "exporting"
              ? progress && progress.total > 0
                ? `Preparing… ${fmtNumber(progress.done)} of ${fmtNumber(progress.total)}`
                : "Preparing…"
              : "Download CSV"}
          </button>
          {(spec.source === "people" || spec.source === "firms") && (
            <label className="ask-act">
              <span className="sr-only">Act on these</span>
              <select
                value=""
                disabled={!result.total || busy !== null}
                onChange={(e) => {
                  if (e.target.value) onAct(e.target.value as ActionKind);
                }}
              >
                <option value="">Act on these…</option>
                {ACTION_KINDS.map((a) => (
                  <option key={a} value={a}>
                    {ACTION_LABELS[a]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      {card}
      {panel === "columns" && (
        <ColumnPicker
          schema={schema}
          current={cols.map((c) => c.name)}
          onApply={(names) => {
            setPanel(null);
            onRun(withColumns(spec, names));
          }}
          onCancel={() => setPanel(null)}
        />
      )}
      {panel === "save" && (
        <SaveForm
          initial={asked ?? result.summary}
          listName={spec.source === "people" ? "People" : "Companies"}
          onSave={(name, shared) => {
            setPanel(null);
            onSave(name, shared);
          }}
          onCancel={() => setPanel(null)}
        />
      )}
      <Notice text={notice?.text ?? null} tone={notice?.tone} />
      {savedView && (
        <p className="ask-saved-link">
          <Link className="btn btn-small" to={viewHref(savedView)}>
            Open “{savedView.name}” on {savedView.source === "people" ? "People" : "Companies"}
          </Link>
        </p>
      )}

      {result.total === 0 ? (
        <div className="state state-empty">
          <p className="state-title">No {noun} match. Try removing a filter.</p>
        </div>
      ) : (
        <ResultTable result={result} columns={cols} disabled={busy !== null} onSort={(field, type) => onRun(toggleSort(spec, field, type))} />
      )}
    </div>
  );
}

function humanSource(s: string): string {
  return { firms: "Firms", people: "People", activity: "Sends and replies", deals: "Deals", meetings: "Meetings", events: "Events", industry_events: "Industry events", networks: "Networks", partners: "Partners" }[s] ?? s;
}

// ------------------------------------------------------------------ filter chips
function FilterChip({ filter, disabled, onChange, onRemove }: { filter: AskSpec["filters"][number]; disabled: boolean; onChange: (v: unknown) => void; onRemove: () => void }) {
  const label = filterLabel(filter);
  const editable = !NO_VALUE_OPS.includes(filter.op);
  const [editing, setEditing] = useState(false);
  const raw = Array.isArray(filter.value) ? filter.value.join(", ") : String(filter.value ?? "");
  const [text, setText] = useState(raw);
  const id = useId();
  const commit = () => {
    setEditing(false);
    if (text.trim() && text.trim() !== raw) onChange(parseValue(filter.op, text));
  };
  return (
    <span className="fchip">
      <span className="fchip-field">{label.field}</span>
      {label.op && <span className="fchip-op">{label.op}</span>}
      {editable ? (
        editing ? (
          <>
            <label htmlFor={id} className="sr-only">
              {`${label.field} ${label.op}`}
            </label>
            <input
              id={id}
              className="fchip-input"
              autoFocus
              value={text}
              size={Math.max(4, text.length + 1)}
              onChange={(e) => setText(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                }
                if (e.key === "Escape") {
                  setText(raw);
                  setEditing(false);
                }
              }}
            />
          </>
        ) : (
          <button type="button" className="fchip-value" disabled={disabled} onClick={() => setEditing(true)} aria-label={`Change ${label.field} ${label.op} ${label.value}`}>
            {label.value}
          </button>
        )
      ) : (
        label.value && <span className="fchip-value fchip-fixed">{label.value}</span>
      )}
      <button type="button" className="fchip-x" disabled={disabled} onClick={onRemove} aria-label={`Remove ${label.field} ${label.op} ${label.value ?? ""}`.replace(/\s+/g, " ").trim()}>
        ×
      </button>
    </span>
  );
}

function AddFilter({ schema, onAdd, onCancel }: { schema: AskColumn[]; onAdd: (f: { field: string; op: string; value?: unknown }) => void; onCancel: () => void }) {
  const fields = schema.filter((c) => c.name !== "id" && c.name !== "firm_id");
  const [field, setField] = useState(fields[0]?.name ?? "");
  const type = fields.find((c) => c.name === field)?.type ?? "text";
  const ops = opsForType(type);
  const [op, setOp] = useState(ops[0] ?? "contains");
  const [value, setValue] = useState("");
  useEffect(() => {
    if (!ops.includes(op)) setOp(ops[0] ?? "contains");
  }, [field]);
  const needsValue = !NO_VALUE_OPS.includes(op);
  if (!fields.length) return <p className="ask-panel muted">Loading the fields…</p>;
  return (
    <form
      className="ask-panel ask-addform"
      onSubmit={(e) => {
        e.preventDefault();
        if (needsValue && !value.trim()) return;
        onAdd(needsValue ? { field, op, value: parseValue(op, value) } : { field, op });
      }}
    >
      <label className="field">
        <span className="field-label">Field</span>
        <select value={field} onChange={(e) => setField(e.target.value)}>
          {fields.map((c) => (
            <option key={c.name} value={c.name}>
              {columnLabel(c.name)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Condition</span>
        <select value={op} onChange={(e) => setOp(e.target.value)}>
          {ops.map((o) => (
            <option key={o} value={o}>
              {OP_LABELS[o] ?? o}
            </option>
          ))}
        </select>
      </label>
      {needsValue && (
        <label className="field">
          <span className="field-label">Value</span>
          <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={/days/.test(op) ? "7" : op === "in" ? "A, B" : ""} />
        </label>
      )}
      <div className="ask-panel-actions">
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Add filter
        </button>
      </div>
    </form>
  );
}

function ColumnPicker({ schema, current, onApply, onCancel }: { schema: AskColumn[]; current: string[]; onApply: (names: string[]) => void; onCancel: () => void }) {
  const [chosen, setChosen] = useState<string[]>(current);
  const fields = schema.filter((c) => c.name !== "id" && c.name !== "firm_id");
  if (!fields.length) return <p className="ask-panel muted">Loading the columns…</p>;
  const toggle = (n: string) => setChosen((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));
  return (
    <fieldset className="ask-panel ask-columns">
      <legend>Columns to show</legend>
      <div className="ask-columns-grid">
        {fields.map((c) => (
          <label key={c.name} className="check">
            <input type="checkbox" checked={chosen.includes(c.name)} onChange={() => toggle(c.name)} />
            <span>{columnLabel(c.name)}</span>
          </label>
        ))}
      </div>
      <div className="ask-panel-actions">
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={!chosen.length} onClick={() => onApply(fields.map((f) => f.name).filter((n) => chosen.includes(n)))}>
          Show these columns
        </button>
      </div>
    </fieldset>
  );
}

function SaveForm({ initial, listName, onSave, onCancel }: { initial: string; listName: string; onSave: (name: string, shared: boolean) => void; onCancel: () => void }) {
  const [name, setName] = useState(initial.slice(0, 80));
  const [shared, setShared] = useState(false);
  return (
    <form
      className="ask-panel ask-saveform"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSave(name.trim(), shared);
      }}
    >
      <label className="field">
        <span className="field-label">Name</span>
        <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus />
        <span className="field-hint">Saved as a view on {listName}, on any device. It re-runs with fresh data each time.</span>
      </label>
      <fieldset className="field ask-share">
        <legend className="field-label">Who sees it</legend>
        <label className="check">
          <input type="radio" name="ask-share" checked={!shared} onChange={() => setShared(false)} />
          <span>Just me</span>
        </label>
        <label className="check">
          <input type="radio" name="ask-share" checked={shared} onChange={() => setShared(true)} />
          <span>Everyone in the business</span>
        </label>
      </fieldset>
      <div className="ask-panel-actions">
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
          Save as a view
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ the table
function cellText(v: unknown, type: string): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (/timestamp|date/.test(type)) return fmtDate(String(v));
  if (typeof v === "number") return fmtNumber(v, 1);
  if (typeof v === "object") return JSON.stringify(v);
  const t = String(v);
  return /^[a-z0-9]+(_[a-z0-9]+)+$/.test(t) ? humanise(t) : t;
}

function ResultTable({ result, columns, disabled, onSort }: { result: AskResult; columns: AskColumn[]; disabled: boolean; onSort: (field: string, type: string) => void }) {
  const sort = result.spec.sort?.[0];
  const source = String(result.spec.source);
  const linkFor = (col: string, row: Record<string, unknown>): string | null => {
    if (source === "firms" && col === "name" && row.id) return `/contacts/companies/${String(row.id)}`;
    if (col === "firm" && row.firm_id) return `/contacts/companies/${String(row.firm_id)}`;
    if (source === "people" && col === "name" && row.id) return `/contacts/people/${String(row.id)}`;
    return null;
  };
  return (
    <div className="table-wrap ask-table" role="region" aria-label="Results" tabIndex={0}>
      <table>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.field === c.name;
              return (
                <th key={c.name} scope="col" aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" className="th-sort" disabled={disabled} onClick={() => onSort(c.name, c.type)}>
                    {columnLabel(c.name)}
                    <span className="th-arrow" aria-hidden="true">
                      {active ? (sort!.dir === "asc" ? "▲" : "▼") : ""}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={String(row.id ?? i)}>
              {columns.map((c, j) => {
                const href = linkFor(c.name, row);
                const text = cellText(row[c.name], c.type);
                return (
                  <td key={c.name} className={j === 0 ? "ask-first" : /int|numeric|double/.test(c.type) ? "num" : undefined}>
                    {href && text !== "—" ? <Link to={href}>{text}</Link> : text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
